import type { Editor } from "@tiptap/core"

import {
  findParentNodes,
  findRow,
  findTable,
  getTableColumnCount,
  getTableRowCount,
} from "../utils"

/**
 * Row utilities
 */
export const isFirstRow = (editor: Editor) => {
  const { table, row } = findParentNodes(editor.state.selection, "table", "row")
  if (!table || !row) return false

  // Check if the current row is the first child of the table
  return table.node.firstChild === row.node
}

export function isFirstOrLastRow(editor: Editor) {
  const { table, row } = findParentNodes(editor.state.selection, "table", "row")
  if (!table || !row) return { isFirstRow: false, isLastRow: false }

  return {
    isFirstRow: table.node.firstChild === row.node,
    isLastRow: table.node.lastChild === row.node,
  }
}

export function isSecondRow(editor: Editor) {
  const { table, row } = findParentNodes(editor.state.selection, "table", "row")
  if (!table || !row) return false

  return table.node.child(1) === row.node
}

export const isHeaderRowToggled = (editor: Editor) => {
  const table = findTable(editor.state.selection)?.node
  if (!table || table.type.name !== "table") return false

  const row = table.firstChild
  if (!row || row.type.name !== "tableRow") return false

  // Checks that each cell in the first row is a header cell
  for (let columnIndex = 0; columnIndex < row.childCount; columnIndex++) {
    const cell = row.child(columnIndex)
    if (cell?.type.name !== "tableHeader") {
      return false
    }
  }

  return true
}

export const isSingleRow = (editor: Editor) => {
  return getTableRowCount(editor) === 1
}

/**
 * Column utilities
 */
export const isFirstColumn = (editor: Editor) => {
  const columnIndex = editor.state.selection.$from.index(-1)
  return columnIndex === 0
}

export function isSecondColumn(editor: Editor) {
  const { table, cell } = findParentNodes(
    editor.state.selection,
    "table",
    "cell",
  )
  if (!table || !cell) return false

  return table.node.child(0).child(1) === cell.node
}

export function isFirstOrLastColumn(editor: Editor) {
  const row = findRow(editor.state.selection)?.node
  const columnIndex = editor.state.selection.$from.index(-1)

  if (!row) return { isFirstColumn: false, isLastColumn: false }

  return {
    isFirstColumn: columnIndex === 0,
    isLastColumn: columnIndex === row.childCount - 1,
  }
}

export const isHeaderColumnToggled = (editor: Editor) => {
  const table = findTable(editor.state.selection)?.node
  if (!table || table.type.name !== "table") return false

  // Checks that each cell in the first column is a header cell
  for (let rowIndex = 0; rowIndex < table.childCount; rowIndex++) {
    const row = table.child(rowIndex)
    const cell = row.firstChild
    if (cell?.type.name !== "tableHeader") {
      return false
    }
  }

  return true
}

export const isSingleColumn = (editor: Editor) => {
  return getTableColumnCount(editor) === 1
}
